import type { AdsConfig, Me } from '@/api/types';
import { obtainConsent } from './consent';
import { FakeAdProvider } from './fake';
import { GptAdProvider, loadGpt } from './gpt';
import type { AdProvider } from './types';

/**
 * SPEC_V5 §0.1: never inside a conversation. No ad network code runs and no
 * card renders on these screens: calls (incl. the post-call decision), Drop
 * pods, chat threads, matching, onboarding, the Safety Center, moderation and
 * reports. Suspended/banned screens are overlays and are checked separately
 * (useModeration). The between-calls interstitial is the one deliberate
 * exception: it runs at the moment the person leaves /call/* (after the call
 * ended and after the decision), see betweenCalls.ts.
 */
const EXCLUDED: RegExp[] = [
  /^\/call(\/|$)/,
  /^\/drops\/[^/]+\/pod(\/|$)/,
  /\/pod(\/|$)/,
  /^\/chats(\/|$)/,
  /^\/friends\/[^/]+\/chat(\/|$)/,
  /^\/match(\/|$)/,
  /^\/onboarding(\/|$)/,
  /^\/safety(\/|$)/,
  /^\/mod(\/|$)/,
  /^\/reports?(\/|$)/,
  /^\/suspended(\/|$)/,
  /^\/banned(\/|$)/,
];

export function isAdExcludedPath(pathname: string): boolean {
  return EXCLUDED.some((r) => r.test(pathname));
}

export function isPlusMe(me: Me | null | undefined): boolean {
  return me?.plan === 'plus';
}

/** Ads exist for this person at all: config on, server says eligible, not Plus (subscription or day pass). */
export function adsOnFor(ads: AdsConfig | null | undefined, me: Me | null | undefined): ads is AdsConfig {
  return !!ads && !!me && me.ads_eligible === true && !isPlusMe(me);
}

type Factory = (ads: AdsConfig) => Promise<AdProvider | null>;

const isTestEnv = import.meta.env.MODE === 'test';
const isDev = !!import.meta.env.DEV;

async function defaultFactory(ads: AdsConfig): Promise<AdProvider | null> {
  // Vitest never touches the network: scripted fake unless a test installs its own.
  if (isTestEnv) return new FakeAdProvider();
  const consent = await obtainConsent(ads.network_code);
  // No CMP answer: dev/test serve non-personalised; production shows nothing.
  if (!consent.ready && !(ads.test_mode || isDev)) return null;
  try {
    const gt = await loadGpt();
    return new GptAdProvider(gt, ads, consent);
  } catch {
    // Blocked/offline GPT: test mode keeps the flows usable with neutral placeholders.
    return ads.test_mode ? new FakeAdProvider({ interactive: true }) : null;
  }
}

let factory: Factory = defaultFactory;
let current: Promise<AdProvider | null> | null = null;
let resolved: AdProvider | null = null;
let factoryCalls = 0;
let generation = 0;

/**
 * The page's one provider, created on first need. Only call this after every
 * gate passed (adsOnFor + not excluded + not suspended); consent and GPT load
 * happen here, never earlier.
 */
export function getAdProvider(ads: AdsConfig): Promise<AdProvider | null> {
  if (!current) {
    factoryCalls += 1;
    const gen = generation;
    current = factory(ads)
      .catch(() => null)
      .then((p) => {
        // Reset while loading: this provider belongs to the old config.
        if (gen !== generation) {
          p?.destroyAll();
          return null;
        }
        resolved = p;
        return p;
      });
  }
  return current;
}

/** The provider if one already exists (no loading). */
export function peekAdProvider(): AdProvider | null {
  return resolved;
}

/**
 * Tear every slot down and forget the provider, so the next `getAdProvider`
 * builds one from the current config. Used when an admin switches ads (or a
 * placement) at runtime (ARCHITECTURE §22). GPT and consent loads are cached,
 * so a rebuild is cheap.
 */
export function resetAdProvider(): void {
  try {
    resolved?.destroyAll();
  } catch {
    // A broken provider must never block the teardown.
  }
  current = null;
  resolved = null;
  generation += 1;
}

/** Tests: install a provider factory (e.g. `() => Promise.resolve(new FakeAdProvider({...}))`). */
export function __setAdProviderFactory(f: Factory | null): void {
  factory = f ?? defaultFactory;
  current = null;
  resolved = null;
  factoryCalls = 0;
  generation += 1;
}

export function __adProviderFactoryCalls(): number {
  return factoryCalls;
}
